import React from 'react';
import './DateFilter.css';

import { DateOptions } from './constants';

import Filter from '../Filter/Filter';

import CalendarIcon from '../../assets/Icons/CalendarIcon.svg?react';
import DatePicker, { registerLocale } from 'react-datepicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/ru';

import { ru as dateFnsRu } from 'date-fns/locale/ru';
import { enGB as dateFnsEn } from 'date-fns/locale/en-GB';

registerLocale('ru', dateFnsRu);
registerLocale('en', dateFnsEn);

export type CustomDateFilterValue = {
  fromDate: string;
  toDate: string;
};

type Props = {
  value?: number;
  customValue?: CustomDateFilterValue;
  onChange: (value?: number, customValue?: CustomDateFilterValue) => void;
};

const isValidDate = (date: Date | null | undefined) =>
  date instanceof Date && !Number.isNaN(date.getTime());

const getDateFormat = (lang: string) => (lang.startsWith('ru') ? 'dd.MM.yyyy' : 'dd/MM/yyyy');

const DateFilter = React.memo(({ onChange, value, customValue }: Props) => {
  const { t, i18n } = useTranslation('translation');
  const [lang, setLang] = React.useState(i18n.language);

  React.useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language]);

  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const dateFormat = getDateFormat(lang);

  React.useEffect(() => {
    if (!customValue) {
      setDateRange([null, null]);
    } else {
      setDateRange([new Date(customValue.fromDate), new Date(customValue.toDate)]);
    }
  }, [customValue]);

  const handleChange = React.useCallback(
    (id?: number) => {
      onChange(id);
    },
    [onChange],
  );

  const handleChangeCustomDateFilter = React.useCallback(
    (value: [Date | null, Date | null] | null) => {
      setDateRange(value ?? [null, null]);
    },
    [],
  );

  const commitCustomDateRange = React.useCallback(() => {
    if (!isValidDate(startDate) && !isValidDate(endDate)) {
      return;
    }

    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      if (customValue) {
        setDateRange([new Date(customValue.fromDate), new Date(customValue.toDate)]);
      } else {
        setDateRange([null, null]);
      }
      return;
    }

    const from = dayjs(startDate);
    const to = dayjs(endDate);
    const normalizedFrom = from.isAfter(to) ? to : from;
    const normalizedTo = from.isAfter(to) ? from : to;

    onChange(undefined, {
      fromDate: normalizedFrom.format('YYYY-MM-DD'),
      toDate: normalizedTo.format('YYYY-MM-DD'),
    });
  }, [customValue, endDate, onChange, startDate]);

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitCustomDateRange();
      }
    },
    [commitCustomDateRange],
  );

  return (
    <Filter
      icon={<CalendarIcon />}
      label={t('dates')}
      options={DateOptions}
      value={value}
      onChange={handleChange}
      additionalContent={
        <div className="DateFilter__Picker StatsHeader__DatePicker">
          <DatePicker
            locale={lang.startsWith('ru') ? 'ru' : 'en'}
            className="DateFilter__Input"
            placeholderText={t('select_date_range')}
            showPopperArrow={false}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            dateFormat={dateFormat}
            isClearable
            allowSameDay
            shouldCloseOnSelect={false}
            onChange={handleChangeCustomDateFilter}
            onCalendarClose={commitCustomDateRange}
            onBlur={commitCustomDateRange}
            onKeyDown={handleInputKeyDown}
            excludeDateIntervals={[
              {
                start: new Date(),
                end: new Date('2030-01-01'),
              },
            ]}
          />
        </div>
      }
      allSelectedFunction={() => value == undefined && startDate === null && endDate === null}
    />
  );
});

export default DateFilter;
